import { SessionEvents, ParticipantTypes, SessionTypes, DeviceKinds } from "./types.js";
import { Participant } from "./participant.js";
import { VideoDeviceParticipant } from "./video_device_participant.js";
import { WebRTCConnectionWatcher } from "./issue_detector.js";
import { SessionContext } from "./session_context.js";


/**
 * @typedef {Object} JoinData - see lightfoot webrtc_peer.py for webrtc payload
 * @param {string} result - the id of the room
 * @param {int} device_id - the id of the device
 * @param {DeviceKinds} device_kind - the kind of device (e.g., fixed, mobilecart, kiosk, etc.)
 * @param {string} video_type - channel type??
 * @param {string} channel - channel name??
 * @param {string} peer_id - id of the peer browser user
 * @param {string} sdp - remote sdp of the video device
 * @param {Object[]} ice_candidates - list of ice candidates
 * @param {string} device_version - semver version of the device (from video_device.rb)
 * @param {Object[]} turn_servers - list of turn servers (from video_device.rb)
 * @param {int} meeting_id - the id of the meeting (if applicable - from video_devices_controller.rb)
 */

/**
 * WebRTCSession represents a webRTC session of a connection to a video device. It helps
 * manage the connection to the video device, the patient kiosk, and other users.
 * 
 * Flows:
 * - connect to patient device
 * - connect to patient kiosk browser (via patient device request)
 * - connect to other user (via patient device request)
 */
export class WebRTCSession {
  #eventListeners = new Map()
  #videoDeviceParticipant = null
  #videoDeviceKioskParticipant = null
  #peerBrowserParticipants = new Map()
  #webrtcWatcher = null
  #deviceRtcPeerConnectionConfig = null
  context = null
  peerId = null

  /**
   * Converts a TURN server URL to an RTCIceServer object.
   * @param {string} url 
   * @returns {RTCIceServer}
   */
  buildRTCIceServer(url) {
    const re = /^(turns?:)\/\/([^:]+):([^@]+)@(.+)$/
    const matches = url.match(re);
    if (!matches) {
      console.error(`invalid turn server url: ${url}`);
      return null;
    }
    return {
      username: decodeURIComponent(matches[2]),
      credential: decodeURIComponent(matches[3]),
      urls: `${matches[1]}${matches[4]}`,
    };
  }

  buildRTCPeerConnectionConfig(defaultConfig, turnServers) {
    let connConfig = defaultConfig
    if (turnServers) {
      connConfig = { ...connConfig }
      if (!connConfig.iceServers) {
        connConfig.iceServers = []
      }
      for (const turnServer of turnServers) {
        const iceServer = this.buildRTCIceServer(turnServer)
        if (iceServer) {
          connConfig.iceServers.push(iceServer)
        }
      }
    }
    return connConfig
  }

  /**
   * Adds an event callback
   * @param {string} eventType The type of event
   * @param {string} eventCallback A callback
   */
  on(eventType, eventCallback) {
    this.#eventListeners.set(eventType, eventCallback);
  }

  emit(eventType, ...eventArgs) {
    const callback = this.#eventListeners.get(eventType);
    if (callback) {
      // unwrap varargs and call callback
      callback.apply(null, eventArgs);
    }
  }

  onConnectionError(reason) {
    console.error(`connection error: ${reason}`);
    this.disconnect(reason);
  }

  onSessionEvent(event_name, ...event_args) {
    console.debug(`session event: ${event_name}`);
    this.emit(event_name, ...event_args);
  }

  /**
   * Mutes the local audio (outgoing) stream. Any incoming audio is uneffected.
   */
  muteLocalAudio() {
    this.setLocalAudioEnabled(false);
  }

  /**
   * Unmutes the local audio (outgoing) stream. Any incoming audio is uneffected.
   */
  unmuteLocalAudio() {
    this.setLocalAudioEnabled(true);
  }

  setLocalAudioEnabled(isEnabled) {
    if (this.context?.localMediaStream) {
      this.context.localMediaStream.getAudioTracks().forEach((trk) => {
        trk.enabled = isEnabled;
      });
    }
  }

  /*
   * Invite a browser peer to create a connection with this browser peer. This is used in
   * the meeting context where multiple users can connect to a video device.
   */
  async inviteBrowserPeerParticipant(peerId) {
    if (this.#peerBrowserParticipants.has(peerId)) {
      console.error(`already have an existing connection to ${peerId}`);
      return null;
    }
    console.log(`generating offer for peer user ${peerId}`);
    const rtcPeerConnection = new RTCPeerConnection(this.context.options.rtcPeerConnectionConfig);
    const browserParticipant = new Participant({
      peerId,
      context: this.context,
      rtcPeerConnection,
      participantType: ParticipantTypes.otherBrowserPeer,
      displayName: this.context.options.userDisplayName,
    })
    this.#peerBrowserParticipants.set(peerId, browserParticipant);

    browserParticipant.on("disconnect", () => {
      console.debug(`peer ${peerId} disconnected`);
      this.#peerBrowserParticipants.delete(peerId);
      this.emit(SessionEvents.participantDisconnected, browserParticipant);
    })
    browserParticipant.on("session-event", this.onSessionEvent.bind(this))

    browserParticipant.enableLocalIceCandidateCollection()
    const offer = await browserParticipant.createBrowserPeerOffer();
    await rtcPeerConnection.setLocalDescription(offer);
    const descr = rtcPeerConnection.localDescription;
    const localIceCandidates = await browserParticipant.collectLocalIceCandidates()
    const offerPayload = JSON.stringify({
      peer_id: peerId,
      sender_peer_id: this.peerId,
      sender_display_name: this.context.options.userDisplayName,
      type: 'peer-sdp-offer',
      sdp: {
        type: 'offer',
        sdp: descr.sdp
      },
      ice_candidates: localIceCandidates,
    });
    // signal through the video device data channel
    this.#videoDeviceParticipant.dataChannel.send(offerPayload);
    console.info(`sent peer offer to ${peerId}`);
  }

  async onPeerAnswer(remoteParticipantId, remoteSdp, remoteIceCandidates, senderDisplayName) {
    const participant = this.#peerBrowserParticipants.get(remoteParticipantId);
    participant.displayName = senderDisplayName;
    if (!participant) {
      console.error(`no remote participant found for ${remoteParticipantId}`);
    }
    const peerConn = participant.rtcPeerConnection;
    console.debug("creating remote sdp...");
    // remoteSdp includes the type field
    await peerConn.setRemoteDescription(remoteSdp);
    console.debug("remote description set");

    remoteIceCandidates.forEach((cand) => {
      peerConn.addIceCandidate(cand);
      console.debug(`added ice candidate to ${remoteParticipantId} connection`, cand);
    });
  }

  /**
   * Handler for a browser peer offer. This may be a device kiosk browser or another browser peer.
   */
  async onPeerOffer(remoteParticipantId, remoteSdp, remoteIceCandidates, mediaRequest, senderDisplayName) {
    let customLocalMedisStream = null;
    const isKiosk = remoteParticipantId === "kiosk-viewer";

    const rtcPeerConnection = new RTCPeerConnection(this.#deviceRtcPeerConnectionConfig);
    const participant = new Participant({
      peerId: remoteParticipantId,
      context: this.context,
      rtcPeerConnection,
      participantType: isKiosk ? ParticipantTypes.kiosk : ParticipantTypes.otherBrowserPeer,
      displayName: senderDisplayName,
    })
    participant.on("session-event", this.onSessionEvent.bind(this))
    participant.on("disconnect", () => {
      console.debug(`peer ${remoteParticipantId} disconnected`);
      this.#peerBrowserParticipants.delete(remoteParticipantId);
      this.emit(SessionEvents.participantDisconnected, participant);
    })

    if (mediaRequest) {
      if (!isKiosk) {
        console.warn("unexpected media request in peer offer for non-kiosk peer", mediaRequest);
      }
      console.debug(`got media request from peer ${remoteParticipantId}`, mediaRequest);
      // assuming this is for kiosk viewer only for now - need a new stream with different constraints
      if (this.context.localMediaStream.getVideoTracks().length === 0 || mediaRequest.video === false) {
        console.debug("video not requested or user has not allowed video");
      } else {
        // kiosk only needs video
        // Create a new stream with only video tracks from the existing stream
        const mediaStream = new MediaStream();
        this.context.localMediaStream.getVideoTracks().forEach(track => {
          mediaStream.addTrack(track);
        });
        customLocalMedisStream = mediaStream;
        console.debug("created custom local media stream for kiosk", customLocalMedisStream);
      }
    }

    participant.enableLocalIceCandidateCollection()
    await participant.configureRemote(remoteSdp, remoteIceCandidates, customLocalMedisStream);

    console.debug(`creating answer for ${remoteParticipantId}...`);
    // add santity check to avoid timiing issues when not awaiting async functions :(
    if (rtcPeerConnection.signalingState !== 'have-remote-offer') {
      console.error(`unexpected signaling state ${rtcPeerConnection.signalingState} for ${remoteParticipantId}`);
    }

    const localSdpAnswer = await rtcPeerConnection.createAnswer();
    // console.debug(localSdpAnswer.sdp);
    await rtcPeerConnection.setLocalDescription(localSdpAnswer);
    const localIceCandidates = await participant.collectLocalIceCandidates();
    // relay the peer answer through the device data channel
    const payload = {
      type: 'peer-sdp-answer',
      sender_display_name: this.context.options.userDisplayName,
      peer_id: remoteParticipantId,
      sender_peer_id: this.peerId,
      sdp: localSdpAnswer,
      ice_candidates: localIceCandidates,
    };
    this.#videoDeviceParticipant.dataChannel.send(JSON.stringify(payload));
    console.info(`sent peer sdp answer to ${remoteParticipantId}`);

    // const localIceCandidates = [];
    // rtcPeerConnection.onicecandidate = (event) => {
    //   if (event.candidate == null) {
    //     // with trickle ICE, a null indicates there are no more ICE candidates
    //     console.debug("ICE Candidate was null, ICE processing is done", localIceCandidates);
    //     // relay the peer answer through the device data channel
    //     const payload = {
    //       type: 'peer-sdp-answer',
    //       peer_id: remoteParticipantId,
    //       sender_peer_id: this.peerId,
    //       sdp: localSdpAnswer,
    //       ice_candidates: localIceCandidates,
    //     };
    //     this.#videoDeviceParticipant.dataChannel.send(JSON.stringify(payload));
    //     console.info(`sent peer sdp answer to ${remoteParticipantId}`);
    //     return;
    //   }
    //   localIceCandidates.push(event.candidate);
    // };

    if (isKiosk) {
      this.#videoDeviceKioskParticipant = participant;
    } else {
      this.#peerBrowserParticipants.set(remoteParticipantId, participant);
    }
  }

  sendDeviceDataChannelMessage(msg) {
    if (this.#videoDeviceParticipant?.dataChannel?.readyState === 'open') {
      console.info("sending device data channel message", msg, this.#videoDeviceParticipant)
      this.#videoDeviceParticipant.dataChannel.send(JSON.stringify(msg));
    } else {
      console.warn(`device data channel is not open. state=${this.#videoDeviceParticipant?.dataChannel?.readyState}`, msg);
      // TODO: a better user experience may be to not display PTZ controls until data channel is open
      // throw new Error('Device data channel is not open. Cannot send message.');
    }
  }

  sendKioskDataChannelMessage(msg) {
    if (this.#videoDeviceKioskParticipant?.dataChannel?.readyState === 'open') {
      console.info("sending device data channel message", msg, this.#videoDeviceKioskParticipant)
      this.#videoDeviceKioskParticipant.dataChannel.send(JSON.stringify(msg));
    } else {
      console.warn(`device data channel is not open. state=${this.#videoDeviceKioskParticipant?.dataChannel?.readyState}`, msg);
      // throw new Error('Device data channel is not open. Cannot send message.');
    }
  }

  async onVideoDeviceDataChannelMessage(payload) {
    switch (payload.type) {
      case "generate-offer":
        await this.inviteBrowserPeerParticipant(payload.peer_id);
        break;
      case "peer-sdp-offer":
        await this.onPeerOffer(payload.sender_peer_id, payload.sdp, payload.ice_candidates, payload.media_request, payload.sender_display_name);
        break;
      case "peer-sdp-answer":
        await this.onPeerAnswer(payload.sender_peer_id, payload.sdp, payload.ice_candidates, payload.sender_display_name);
        break;
      case "ai-nudge":
        console.info("received ai-nudge", payload);
        this.emit(SessionEvents.nudgeReceived, payload);
        break;
      case "user-bye":
        // assuming connection/track disconnection logic can handle this
        console.info(`received user-bye from ${payload.sender_peer_id}`);
        break;
      default:
        console.error(`unexpected data channel payload type: ${payload.type}`);
    }
  }

  enableWebrtcWatcher(peerConn) {
    const qualityIntervalMs = this.context.options.webrtcQualityEnabled ? 2000 : 0;
    const statsIntervalMs = this.context.options.statsIntervalMs || 0;
    if (qualityIntervalMs > 0 || statsIntervalMs > 0) {
      this.#webrtcWatcher = new WebRTCConnectionWatcher({
        connection: peerConn,
        qualityIntervalMs,
        statsIntervalMs,
      })
      this.#webrtcWatcher.on("quality-updated", (data) => {
        this.emit(SessionEvents.webrtcQualityUpdated, data);
      });
      this.#webrtcWatcher.on("issues", (data) => {
        this.emit(SessionEvents.webrtcIssue, data);
      });
      this.#webrtcWatcher.on("scores-updated", (data) => {
        this.emit(SessionEvents.webrtcNetworkScoreUpdated, data);
      });
      this.#webrtcWatcher.start()
      console.debug("webrtc watcher enabled", qualityIntervalMs, statsIntervalMs);
    } else {
      console.debug("webrtc watcher not enabled");
    }
  }



  /**
   * Starts a session with a connection to the video device.
   * 
   * @param {JoinData} joinData - the join data from the server
   * @param {SessionTypes} type of session (meeting | monitoring | test)
   * @param {MediaStream} localMediaStream - the local media stream
   * @param {SessionContextOptions} options - the session options
   * @returns {Promise<void>}
   */
  async connect(joinData, sessionType, localMediaStream, options = {}) {
    this.context = new SessionContext({
      joinData,
      sessionType,
      localMediaStream,
      options,
    });
    this.peerId = joinData.peer_id;
    this.#deviceRtcPeerConnectionConfig = this.buildRTCPeerConnectionConfig(
      this.context.options.rtcPeerConnectionConfig,
      joinData.turn_servers
    )

    // connect to video device
    const devicePeerConn = new RTCPeerConnection(this.#deviceRtcPeerConnectionConfig);
    const deviceParticipant = new VideoDeviceParticipant({
      peerId: joinData.device_id,
      rtcPeerConnection: devicePeerConn,
      context: this.context,
      videoDeviceId: typeof joinData.device_id === 'number' ? joinData.device_id : joinData.device_id.split('-').slice(-1)[0],  // device_id is in the format "video_device-<id>"
      dataChannelMessageListener: this.onVideoDeviceDataChannelMessage.bind(this),
      sendVideo: sessionType === SessionTypes.meeting && joinData.device_kind === DeviceKinds.kiosk,
    })
    this.#videoDeviceParticipant = deviceParticipant
    deviceParticipant.on("error", this.onConnectionError.bind(this))
    deviceParticipant.on("session-event", this.onSessionEvent.bind(this))

    // deviceParticipant.on("device-datachannel-message", this.onVideoDeviceDataChannelMessage.bind(this))

    devicePeerConn.addEventListener('connectionstatechange', () => {
      console.debug(`${this.peerId} connection state changed to ${devicePeerConn.connectionState}`);
      if (devicePeerConn.connectionState === 'connected') {
        this.enableWebrtcWatcher(devicePeerConn);
        this.emit(SessionEvents.connectSucceeded, this);
        // thisRoom.emit(VideoRoomManager.EVENT_TYPES.connectSucceeded, thisRoom);
      } else if (devicePeerConn.connectionState === 'disconnected' || devicePeerConn.connectionState === 'failed') {
        // this won't be the state if the connection is closed normally (connectionState will be 'closed')
        this.emit(SessionEvents.participantDisconnected, deviceParticipant);
        this.disconnect();
      }
    })
    await deviceParticipant.configureRemote(joinData.sdp, joinData.ice_candidates);
    console.debug("remote sdp set. creating answer...");
    if (devicePeerConn.signalingState !== 'have-remote-offer') {
      console.error(`unexpected signaling state ${devicePeerConn.signalingState}`, deviceParticipant);
    }
    const localSdpAnswer = await devicePeerConn.createAnswer();
    // console.debug(localSdpAnswer.sdp);
    // we don't have a connection to the device yet so we need to signal through the server
    this.emit(SessionEvents.signalRequired, {
      video_type: sessionType,
      signal_type: 'sdp-answer',
      sdp: localSdpAnswer,
    });
    let ice_candidates_batch = [];
    devicePeerConn.addEventListener("icecandidate", (event) => {
      if (event.candidate != null) {
        console.debug("local ICE Candidate", event.candidate);
        if (ice_candidates_batch) {
          ice_candidates_batch.push(event.candidate);
          if (ice_candidates_batch.length === 1) {
            // typically the first 2-3 ICE candidates are cached and returned immediately one after another
            // so wait a tiny bit before sending the initial batch
            setTimeout(() => {
              this.emit(SessionEvents.signalRequired, {
                video_type: sessionType,
                signal_type: 'ice-candidates',
                ice_candidates: ice_candidates_batch,
              });
              ice_candidates_batch = null;
            }, 40);
          }
        } else {
          this.emit(SessionEvents.signalRequired, {
            video_type: sessionType,
            signal_type: 'ice-candidates',
            ice_candidates: [event.candidate],
          });
        }
      } else {
        // with trickle ICE, a null indicates there are no more ICE candidates
        console.debug("local ICE Candidate was null, ICE processing is done");
      }
    });
    await devicePeerConn.setLocalDescription(localSdpAnswer);
  }

  async disconnect(disconnectReason = null) {
    console.debug(`starting diconnect of ${this.peerId}...`);
    if (this.#webrtcWatcher) {
      this.#webrtcWatcher.stop();
      this.#webrtcWatcher = null;
    }
    const selfId = this.context?.joinData?.peer_id;
    this.#peerBrowserParticipants.forEach((participant) => {
      participant.disconnect(disconnectReason);
      if (this.#videoDeviceParticipant.dataChannel) {
        try {
          this.#videoDeviceParticipant.dataChannel.send(JSON.stringify({
            peer_id: selfId,  // send the peer id of the participant that is disconnecting
            type: 'user-bye'
          }));
          console.debug(`sent user-bye from ${selfId} to ${participant.peerId}`);
        } catch (err) {
          console.warn(`error sending user-bye to ${participant.peerId}`, err);
        }
      } else {
        console.warn(`no video device data channel to send user-bye to ${participant.peerId}`);
      }
    })
    this.#peerBrowserParticipants.clear();
    if (this.#videoDeviceKioskParticipant) {
      this.#videoDeviceKioskParticipant.disconnect();
      this.#videoDeviceKioskParticipant = null;
    }
    // video device participant should be closed last in case data channel messages are sent during disconnect
    if (this.#videoDeviceParticipant) {
      this.#videoDeviceParticipant.disconnect();
      this.#videoDeviceParticipant = null;
    }
    try {
      this.emit(SessionEvents.disconnected, disconnectReason);
    } finally {
      this.#eventListeners.clear();
    }
  }


  // TODO: Aimee participant in meetings
  // - use a separate aimee agent that has acces to this session
  // - for Aimee audio going to video device, send request to video device to add a recvonly audio transceiver;
  // device sends new offer with new transceiver. connect the aimee track to the new transceiver and send answer;
  // this can be done with the device's data channel for signaling;
  // NOTE: since the device initiated the connection, it needs to add the transceiver and send a new offer
  // - for audio going to Aimee, use web audio api to connect all audio tracks to a single destination track
  // that is added to the Aimee RTCPeerConnection
}